/**
 * MasterEntityDocumentsTab — shared Documents tab for master entities
 * (Patch 2).
 *
 * Used inside the Settings → Clients / Suppliers / Departments detail panels.
 * Displays two sections:
 *   • Direct attachments — docs/comms anchored to this master entity directly
 *   • Related from projects — docs/comms anchored to project entities that
 *     involve this master (clients via projects.client_id, suppliers via
 *     order_batches.supplier_id; departments have no traverse mapping in v1
 *     and will always show empty)
 *
 * Both sections use the compact row pattern. Click any row → opens the
 * shared detail drawer.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText, Paperclip, Plus } from 'lucide-react';
import { formatRelative } from '@procurement/shared';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Alert } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { AttachmentDetailDrawer } from './detail-drawer';
import { LogCommunicationModal } from './log-communication-modal';
import { UploadDocumentModal } from './upload-document-modal';
import {
  useMasterEntityAttachments,
  type Attachment,
  type AttachmentAnchor,
  type MasterEntityKind,
} from '@/lib/attachments-queries';

interface Props {
  kind: MasterEntityKind;
  id: string;
  /** The entity's display name, used in modal pre-fills + heading. */
  name: string;
}

export function MasterEntityDocumentsTab({ kind, id, name }: Props) {
  const q = useMasterEntityAttachments(kind, id);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [directExpanded, setDirectExpanded] = useState(true);
  const [relatedExpanded, setRelatedExpanded] = useState(true);

  const defaultAnchor: AttachmentAnchor = {
    projectId: null,
    projectItemId: null,
    orderBatchId: null,
    bankGuaranteeId: null,
    inspectionEventId: null,
    clientId: kind === 'CLIENT' ? id : null,
    supplierId: kind === 'SUPPLIER' ? id : null,
    masterDepartmentId: kind === 'DEPARTMENT' ? id : null,
  };

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner className="h-5 w-5 text-tertiary" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <Alert tone="danger">Could not load documents.</Alert>;
  }

  const { direct, related } = q.data;
  const isEmpty = direct.length === 0 && related.length === 0;

  return (
    <>
      <div className="mb-3 flex items-center justify-end gap-2">
        <Button variant="secondary" size="compact" onClick={() => setShowLog(true)}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          Log communication
        </Button>
        <Button variant="primary" size="compact" onClick={() => setShowUpload(true)}>
          <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          Upload document
        </Button>
      </div>

      {isEmpty ? (
        <Card className="p-0">
          <EmptyState
            title={`No documents yet for ${name}`}
            body="Upload an agreement, contract, or correspondence — or log a phone call or letter — to start the trail."
            action={
              <div className="flex gap-2">
                <Button variant="secondary" size="compact" onClick={() => setShowLog(true)}>
                  Log communication
                </Button>
                <Button variant="primary" size="compact" onClick={() => setShowUpload(true)}>
                  Upload document
                </Button>
              </div>
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          <Section
            title={`Direct attachments (${direct.length})`}
            expanded={directExpanded}
            onToggle={() => setDirectExpanded((v) => !v)}
          >
            {direct.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-tertiary">
                Nothing directly attached yet.
              </div>
            ) : (
              <ul className="divide-y divide-border-default">
                {direct.map((a) => (
                  <AttachmentRow key={a.id} a={a} onOpen={() => setSelectedId(a.id)} />
                ))}
              </ul>
            )}
          </Section>

          {kind !== 'DEPARTMENT' ? (
            <Section
              title={`Related from projects (${related.length})`}
              expanded={relatedExpanded}
              onToggle={() => setRelatedExpanded((v) => !v)}
            >
              {related.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-tertiary">
                  No related project attachments yet.
                </div>
              ) : (
                <ul className="divide-y divide-border-default">
                  {related.map((a) => (
                    <AttachmentRow key={a.id} a={a} onOpen={() => setSelectedId(a.id)} />
                  ))}
                </ul>
              )}
            </Section>
          ) : null}
        </div>
      )}

      {showUpload ? (
        <UploadDocumentModal
          open={showUpload}
          onClose={() => setShowUpload(false)}
          projectId={null}
          defaultAnchor={defaultAnchor}
        />
      ) : null}
      {showLog ? (
        <LogCommunicationModal
          open={showLog}
          onClose={() => setShowLog(false)}
          projectId={null}
          defaultAnchor={defaultAnchor}
        />
      ) : null}

      <AttachmentDetailDrawer
        attachmentId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}

function Section({
  title,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-0">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 border-b border-border-default px-3 py-2 text-left hover:bg-subtle"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-tertiary" strokeWidth={1.5} />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-tertiary" strokeWidth={1.5} />
        )}
        <span className="text-xs font-semibold uppercase tracking-wide text-secondary">
          {title}
        </span>
      </button>
      {expanded ? <div>{children}</div> : null}
    </Card>
  );
}

function AttachmentRow({ a, onOpen }: { a: Attachment; onOpen: () => void }) {
  const isDoc = a.kind === 'DOCUMENT';
  const title = isDoc ? (a.displayName ?? '—') : (a.subject ?? '—');
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-subtle"
      >
        {isDoc ? (
          <FileText className="h-3.5 w-3.5 shrink-0 text-tertiary" strokeWidth={1.5} />
        ) : (
          <Paperclip className="h-3.5 w-3.5 shrink-0 text-tertiary" strokeWidth={1.5} />
        )}
        <span className="min-w-0 flex-1 truncate font-medium text-primary">{title}</span>
        <span className="shrink-0 text-tertiary">
          {a.projectId ? 'Project' : 'Direct'}
        </span>
        <span className="shrink-0 text-secondary tabular-nums">
          {formatRelative(a.occurredAt ?? a.documentDate ?? a.createdAt)}
        </span>
      </button>
    </li>
  );
}
